import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import type { Photo } from '@/types';
import LikeButton from './LikeButton';
import CommentList from './CommentList';
import { Modal } from '@/components/ui/Modal';
import { photoThumbnail } from '@/lib/api';
import { recordView } from '@/hooks/useGallery';

interface MasonryGalleryProps {
  photos: Photo[];
  mockImageFn?: (photo: Photo) => string;
}

const PAGE_SIZE = 50;

function getImageUrl(photo: Photo, mockFn?: (p: Photo) => string): string {
  if (mockFn) {
    return mockFn(photo);
  }
  return photoThumbnail(photo);
}

export default function MasonryGallery({ photos, mockImageFn }: MasonryGalleryProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const visiblePhotos = photos.slice(0, visibleCount);
  const hasMore = visibleCount < photos.length;

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const handleOpenPhoto = (photo: Photo) => {
    setSelectedPhoto(photo);
    recordView(photo.id);
  };

  return (
    <>
      <div className="container-page max-w-4xl mx-auto px-sm sm:px-md">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-sm sm:gap-md md:gap-lg justify-center">
          {visiblePhotos.map((photo, i) => (
            <motion.div
              key={photo.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.03 }}
              onClick={() => handleOpenPhoto(photo)}
              className="group cursor-pointer overflow-hidden rounded-md border border-border bg-bg-alt flex flex-col justify-between transition-all hover:border-accent/40"
              style={{ boxShadow: 'var(--shadow-sm)' }}
            >
              {/* Casetă cu proporție 4/3, tăiere minimă sus/jos */}
              <div className="w-full aspect-[4/3] overflow-hidden bg-bg p-2 relative">
                <img
                  src={getImageUrl(photo, mockImageFn)}
                  alt={photo.caption || `Photo by ${photo.uploader_name}`}
                  loading="lazy"
                  className="h-full w-full object-cover rounded-sm transition-transform duration-slow group-hover:scale-105"
                />
              </div>
              <div className="p-sm">
                <div className="flex items-center justify-between gap-1">
                  <p className="text-sm font-medium truncate">{photo.uploader_name}</p>
                  <div className="flex items-center gap-sm text-xs text-fg-soft shrink-0">
                    <div onClick={(e) => e.stopPropagation()}>
                      <LikeButton photoId={photo.id} initialCount={photo.likes_count} compact />
                    </div>
                    <span className="inline-flex items-center gap-0.5">
                      <span aria-hidden>💬</span>
                      {photo.comments_count}
                    </span>
                    <span className="inline-flex items-center gap-0.5">
                      <span aria-hidden>👁</span>
                      {photo.views_count ?? 0}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {hasMore && (
          <div ref={sentinelRef} className="py-xl flex flex-col items-center justify-center gap-2">
            <p className="font-mono text-xs text-fg-soft">
              Afișate {visiblePhotos.length} din {photos.length} poze
            </p>
            <button
              type="button"
              onClick={loadMore}
              className="rounded-full border border-border bg-bg-alt px-5 py-2 font-mono text-xs text-fg hover:border-accent hover:text-accent transition-colors shadow-sm cursor-pointer"
            >
              Încarcă următoarele 50 de poze ↓
            </button>
          </div>
        )}

        {!hasMore && photos.length > PAGE_SIZE && (
          <div className="py-xl text-center">
            <p className="font-mono text-xs text-fg-soft">
              Ai ajuns la final · toate cele {photos.length} de poze sunt afișate ✨
            </p>
          </div>
        )}
      </div>

      <Modal open={!!selectedPhoto} onClose={() => setSelectedPhoto(null)}>
        {selectedPhoto && (
          <div className="max-h-[85vh] overflow-y-auto">
            <img
              src={getImageUrl(selectedPhoto, mockImageFn)}
              alt={selectedPhoto.caption || ''}
              className="max-h-[55vh] w-full rounded-md object-contain bg-bg"
            />
            <div className="mt-md flex items-center justify-between">
              <div>
                <p className="font-serif text-lg leading-tight">{selectedPhoto.uploader_name}</p>
                {selectedPhoto.caption && (
                  <p className="mt-1 text-sm text-fg-soft">{selectedPhoto.caption}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-fg-soft">
                  👁 {selectedPhoto.views_count ?? 0}
                </span>
                <LikeButton photoId={selectedPhoto.id} initialCount={selectedPhoto.likes_count} />
              </div>
            </div>

            {/* Comment Section */}
            <CommentList photoId={selectedPhoto.id} />
          </div>
        )}
      </Modal>
    </>
  );
}
